import _ from "lodash";
/**
 * Initializes WPD Utils module.
 */
function init(wpd) {
	const RATIO_TYPE_VALUES = [
		"",
		"Change from baseline",
		"Percent change from baseline",
		"Ratio from baseline",
	];

	wpd.utils.initializeRatioTypesSelect = (input, selectedValue) => {
		const labels = [
			"Not a ratio",
			"Change from baseline",
			"Percent change from baseline",
			"Ratio from baseline",
			"Other...",
		];
		const values = [...RATIO_TYPE_VALUES, "__other__"];

		let defaultValue = selectedValue ?? "";
		if (selectedValue && typeof selectedValue === "boolean") {
			// backwards compatibility
			defaultValue = "Change from baseline";
		}

		// If the saved value is custom (not in predefined list), show "Other..." selected
		const isCustom = defaultValue && !RATIO_TYPE_VALUES.includes(defaultValue);
		input.innerHTML = wpd.utils.createOptionsHTML(
			labels,
			values,
			isCustom ? "__other__" : defaultValue,
		);

		// Set up the Other... swap behavior and return the value getter
		return wpd.utils.setupRatioTypeOther(input, isCustom ? defaultValue : "");
	};

	/**
	 * Sets up "Other..." swap behavior on a ratio type <select>.
	 * When "Other..." is selected, the <select> is hidden and replaced with
	 * a text <input> + back button. Returns a function to get the current value.
	 */
	wpd.utils.setupRatioTypeOther = (selectEl, initialCustomValue) => {
		// Find the sibling .ratio-type-other wrapper (pre-rendered in elements.js)
		const wrapper = selectEl.parentElement.querySelector(".ratio-type-other");
		if (!wrapper) return () => selectEl.value;

		const backBtn = wrapper.querySelector(".ratio-type-back");
		const textInput = wrapper.querySelector(".ratio-type-custom");

		if (initialCustomValue) {
			selectEl.style.display = "none";
			wrapper.style.display = "inline-flex";
			textInput.value = initialCustomValue;
		}

		// When "Other..." is selected, swap to text input
		selectEl.addEventListener("change", () => {
			if (selectEl.value === "__other__") {
				selectEl.style.display = "none";
				wrapper.style.display = "inline-flex";
				textInput.value = "";
				textInput.focus();
			}
		});

		// Back button returns to select
		backBtn.addEventListener("click", () => {
			wrapper.style.display = "none";
			selectEl.style.display = "";
			selectEl.value = "";
			textInput.value = "";
		});

		// Return a getter for the current value (works in both modes)
		const getValue = () => {
			if (wrapper.style.display !== "none") {
				return textInput.value;
			}
			const val = selectEl.value;
			return val === "__other__" ? "" : val;
		};

		// Attach getter to the select element for easy access
		selectEl._getRatioTypeValue = getValue;
		return getValue;
	};

	wpd.utils.createOptionsHTML = function (
		labels,
		values,
		selectedValue,
		disabledIndexes = [],
	) {
		if (labels.length !== values.length) {
			console.error("labels and values length mismatch");
		}

		let optionsHTML = "";
		for (let i = 0; i < labels.length; i++) {
			optionsHTML += '<option value="' + values[i] + '"';
			if (values[i] === selectedValue) optionsHTML += " selected";
			if (disabledIndexes.includes(values[i])) optionsHTML += " disabled";
			optionsHTML += ">" + labels[i] + "</option>";
		}
		return optionsHTML;
	};

	/**
	 * Checks if a given file is a supported file type.
	 * @param {File} file - File to check
	 * @returns {Boolean} - Whether or not given file is supported
	 */
	wpd.utils.isSupportedFile = (file) => {
		return [
			"application/pdf",
			"image/bmp",
			"image/gif",
			"image/jpeg",
			"image/png",
		].includes(file.type);
	};

	wpd.utils.escapeSlashes = (string) =>
		string ? `${string}`.replaceAll("/", "\\") : "";

	wpd.utils.unescapeSlashes = (string) =>
		string ? `${string}`?.replaceAll("\\", "/") : "";

	wpd.utils.oneLine = (string) => {
		return _.chain(string).replace(/\s+/g, " ").trim().value();
	};

	/**
	 * Check if provided key is a word. For use with _.omitBy.
	 * @param  {Any}     value - Unused variable
	 * @param  {Any}     key   - Parameter to check if NaN
	 * @return {Boolean}       - Whether or not key is word
	 */
	wpd.utils.isWord = (value, key) => isNaN(key);

	/**
	 * Rounds a finite number to the given number of decimal places. Non-finite
	 * numbers and non-number values are returned unchanged so that existing
	 * user-edited string values survive re-rounding during axis-metadata edits.
	 * @param {*} number - Value to round if finite-numeric; otherwise passed through
	 * @param {Number} decimalPlaces - Number of decimal places
	 * @returns {*}
	 */
	wpd.utils.toDecimalPlaces = (number, decimalPlaces) => {
		// Pass non-numeric values through unchanged. A data point's stored
		// value may be a user-edited string like "BL" that shouldn't be
		// coerced to NaN when this helper is called to re-round values
		// during a data-type / decimals / unit edit.
		if (typeof number !== "number" || !isFinite(number)) {
			return number;
		}

		return parseFloat(number.toFixed(decimalPlaces));
	};

	/**
	 * Fallback significant figures for a log-scale axis when no decimals value is
	 * provided. Normally the axis "decimal points" field drives the sig-fig count
	 * (see roundAxisValue); this only applies when that value is missing.
	 */
	wpd.utils.LOG_AXIS_DEFAULT_SIG_FIGS = 4;

	/**
	 * Rounds a finite number to the given number of significant figures. Non-finite
	 * numbers and non-number values are returned unchanged (mirrors toDecimalPlaces)
	 * so existing user-edited string values like "BL" survive re-rounding.
	 * @param {*} number - Value to round if finite-numeric; otherwise passed through
	 * @param {Number} sigFigs - Number of significant figures
	 * @returns {*}
	 */
	wpd.utils.toSignificantFigures = (number, sigFigs) => {
		if (typeof number !== "number" || !isFinite(number)) {
			return number;
		}
		if (number === 0) {
			return 0;
		}
		return parseFloat(number.toPrecision(sigFigs));
	};

	/**
	 * Rounds an axis-derived value. On a log axis the value is rounded to a number
	 * of SIGNIFICANT FIGURES equal to the axis "decimal points" setting (constant
	 * relative precision across magnitudes - a fixed decimal floor would collapse
	 * values near or below the axis minimum to 0). toPrecision accepts 1-100 sig
	 * figs, so the count is clamped (e.g. 0 decimals becomes 1 sig fig). On a
	 * linear axis the value is rounded to the given number of decimal places
	 * (unchanged behavior).
	 * @param {*} number - Value to round if finite-numeric; otherwise passed through
	 * @param {Boolean} isLog - Whether the value's axis is log-scaled
	 * @param {Number} decimalPlaces - Decimal places (linear) / significant figures (log)
	 * @returns {*}
	 */
	wpd.utils.roundAxisValue = (number, isLog, decimalPlaces) => {
		if (isLog) {
			const sigFigs = Math.max(
				1,
				Math.min(
					100,
					Math.round(decimalPlaces ?? wpd.utils.LOG_AXIS_DEFAULT_SIG_FIGS),
				),
			);
			return wpd.utils.toSignificantFigures(number, sigFigs);
		}
		return wpd.utils.toDecimalPlaces(number, decimalPlaces);
	};

	/**
	 * Check if a given metadata value is "empty"; this needs to return
	 * true for the value 0, but false for any form of empty value.
	 * @param {Any} value - Value to check
	 */
	wpd.utils.isEmptyValue = (value) => {
		return value === null || value === undefined || value === "";
	};

	/**
	 * Check if a given object is empty.
	 * @param {Object} object - Object to check
	 * @returns {Boolean} - Whether or not object is empty
	 */
	wpd.utils.isEmptyObject = (value) => {
		if (typeof value === "object" && value !== null) {
			return Object.keys(value).length === 0;
		} else {
			throw new Error("Provided value is not an object");
		}
	};

	/**
	 * Deep clone a value using JSON.stringify and JSON.parse. Only works for
	 * serializable data.
	 * @param {*} value - Any value to clone
	 * @returns {*} - Cloned value
	 */
	wpd.utils.deepClone = (value) => JSON.parse(JSON.stringify(value));

	wpd.utils.findDetailsByTagGroupInstanceUuid = (uuid) => {
		const plotData = wpd.appData.getPlotData();
		const documentMetadata = wpd.appData.getFileManager().getMetadata();
		const datasets = plotData.getDatasets();

		for (const dataset of datasets) {
			const dataPoints = dataset.getAllPixels();
			const instanceDataPoints = dataPoints.filter(
				(dataPoint) => dataPoint?.metadata?.tagGroup?.instanceUuid === uuid,
			);

			if (instanceDataPoints.length > 0) {
				const axis = plotData.getAxesForDataset(dataset);

				// assume the type is branch if no type is found
				const type =
					instanceDataPoints.find((dataPoint) => dataPoint?.metadata?.type)
						?.metadata?.type ?? "branch";

				return {
					datasetName: dataset.name,
					axesName: axis.name,
					file: documentMetadata.file.datasets[dataset.name],
					page: documentMetadata.page.datasets[dataset.name],
					type,
				};
			}
		}

		return {};
	};

	/**
	 * Reindex the numeric metadata keys. Leaves other keys in place.
	 * @param {Object} metadata - Metadata to reindex
	 * @returns {Object} - Reindexed metadata
	 */
	wpd.utils.reindexMetadata = (metadata) => {
		const metadataRowKeys = Object.keys(metadata).filter((key) => !isNaN(key));

		// reindex the metadata keys
		metadataRowKeys
			.sort((a, b) => a - b)
			.forEach((key, index) => {
				// to avoid key collisions, first collect values to a temp array, then reassign
				const tempValue = metadata[key];
				delete metadata[key];
				metadata[index] = tempValue;
			});

		return metadata;
	};

	/**
	 * Escapes a string for safe use in JavaScript code within HTML attributes.
	 */
	wpd.utils.escapeForJS = (str) => {
		return JSON.stringify(str).replace(/"/g, "&quot;");
	};

	/**
	 * Switches to the given file and page.
	 * @param {number} fileIndex - Index of the file to switch to
	 * @param {number} pageNumber - Number of the page to switch to (optional)
	 * @returns {Promise} - Promise that resolves when the switch is complete
	 */
	wpd.utils.switchToFileAndPage = async (fileIndex, pageNumber) => {
		// switch file
		const fileManager = wpd.appData.getFileManager();
		const currentFileIndex = fileManager.currentFileIndex();
		if (currentFileIndex !== fileIndex) {
			await fileManager.switch(fileIndex);
		}

		// switch page
		if (wpd.appData.isMultipage() && pageNumber !== undefined) {
			const pageManager = wpd.appData.getPageManager();
			const currentPage = pageManager.currentPage();
			if (currentPage != pageNumber) {
				await pageManager.switch(pageNumber);
			}
		}
	};
}

export { init };
